// 管理员登录页面和注册页面

import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { login } from "./action";
import { styled } from "@mui/material/styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LoginIcon from "@mui/icons-material/Login";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 样式化组件
const AuthContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url("/images/bg-pattern.jpg") no-repeat center center;
  background-size: cover;
  padding: 24px;
`;

// 添加一个外部容器来包含卡片和色块
const CardWrapper = styled(Box)`
  position: relative;
  width: 100%;
  max-width: 1020px; // 比卡片宽20px，每边多10px
  height: 630px; // 比卡片高20px，每边多10px
`;

// 修改 GlassCard 样式，使其在 CardWrapper 内居中
const GlassCard = styled(Box)`
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  bottom: 10px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 24px;
    pointer-events: none;
    z-index: 0;
  }
`;

const FormSide = styled(Box)`
  width: 50%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
`;

// 修改 SlideOverlay 样式，使其相对于 CardWrapper 定位
const SlideOverlay = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 10px; // 初始位置从卡片左边缘开始
  width: 50%;
  background: rgba(0, 0, 0);
  transition: transform 0.8s cubic-bezier(0.77, 0, 0.175, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 40px;
  color: white;
  text-align: center;
  z-index: 10;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
`;

const Title = styled(Typography)`
  font-size: 1.8rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 8px;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
`;

const Subtitle = styled(Typography)`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 24px;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 20px;

  .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: rgba(255, 255, 255, 0.3);
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: rgba(255, 255, 255, 0.5);
      border-width: 1px;
    }
  }

  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.8);

    &.Mui-focused {
      color: rgba(255, 255, 255, 0.95);
    }
  }
`;

const ActionButton = styled(Button)`
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  font-size: 1rem;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const OverlayButton = styled(Button)`
  background-color: transparent;
  border: 1px solid #fff;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  font-size: 1rem;
  color: #fff;
  margin-top: 20px;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const IconContainer = styled(Box)`
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 70px;
  height: 70px;
  margin: 0 auto 20px;
`;

const AuthPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // 登录状态
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginMessage, setLoginMessage] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);

  // 注册状态
  const [regUsername, setRegUsername] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [regMessage, setRegMessage] = useState("");
  const [regError, setRegError] = useState("");

  // 面板切换状态
  const [isRightPanelActive, setIsRightPanelActive] = useState(false);

  // 根据URL参数决定初始面板
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("register") === "true") {
      setIsRightPanelActive(true);
    }

    // 检查是否有来自其他页面的消息
    if (location.state?.message) {
      setLoginMessage(location.state.message);
    }
  }, [location]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginMessage("");
    setLoginError("");
    setLoginSuccess(false);

    // 验证输入
    if (!username.trim()) {
      setLoginError("用户名不能为空");
      setLoginLoading(false);
      return;
    }

    if (!password.trim()) {
      setLoginError("密码不能为空");
      setLoginLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      setLoginSuccess(true);
      setLoginMessage("登录成功！正在跳转到管理页面...");

      // 保存认证信息
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("refreshtoken", response.data.refreshtoken);
      localStorage.setItem("username", username);

      // 更新 Redux 状态
      dispatch(login(username));

      // 延迟一小段时间后导航
      setTimeout(() => {
        // 如果有来源页面，则返回该页面
        if (location.state?.from) {
          navigate(location.state.from);
        } else {
          navigate("/administration"); // 默认导航到管理页面
        }
      }, 1500);
    } catch (error) {
      if (error.response && error.response.data) {
        setLoginError(error.response.data);
      } else {
        setLoginError(error.message);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegMessage("");
    setRegError("");

    // 验证输入
    if (!regUsername.trim()) {
      setRegError("用户名不能为空");
      return;
    }

    if (!regPassword.trim()) {
      setRegError("密码不能为空");
      return;
    }

    if (regPassword !== confirmPassword) {
      setRegError("两次输入的密码不一致");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        username: regUsername,
        password: regPassword,
        secretCode: secretCode,
      });

      setRegMessage("注册成功！正在登录...");

      // 保存认证信息
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("refreshtoken", response.data.refreshtoken);
      localStorage.setItem("username", regUsername);

      // 更新 Redux 状态
      dispatch(login(regUsername));

      // 延迟一小段时间后导航
      setTimeout(() => {
        navigate("/administration");
      }, 1500);
    } catch (error) {
      if (error.response && error.response.data) {
        setRegError(error.response.data);
      } else {
        setRegError("注册失败，请稍后重试");
      }
    }
  };

  return (
    <AuthContainer>
      <CardWrapper>
        <GlassCard>
          {/* 注册表单 - 左侧 */}
          <FormSide>
            <IconContainer>
              <PersonAddIcon
                fontSize="large"
                sx={{ color: "rgba(255, 255, 255, 0.9)", position: "relative", zIndex: 1 }}
              />
            </IconContainer>
            <Title variant="h1">管理员注册</Title>
            <Subtitle variant="subtitle1">创建您的管理员账号</Subtitle>

            {regError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(253, 237, 237, 0.8)",
                }}
              >
                {regError}
              </Alert>
            )}

            {regMessage && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(237, 247, 237, 0.8)",
                }}
              >
                {regMessage}
              </Alert>
            )}

            <form onSubmit={handleRegisterSubmit}>
              <StyledTextField
                label="用户名"
                type="text"
                fullWidth
                variant="outlined"
                value={regUsername}
                onChange={(e) => setRegUsername(e.target.value)}
                required
              />

              <StyledTextField
                label="密码"
                type="password"
                fullWidth
                variant="outlined"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                required
              />

              <StyledTextField
                label="确认密码"
                type="password"
                fullWidth
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />

              <StyledTextField
                label="密保码"
                type="password"
                fullWidth
                variant="outlined"
                value={secretCode}
                onChange={(e) => setSecretCode(e.target.value)}
                required
              />

              <ActionButton
                type="submit"
                variant="contained"
                sx={{
                  background: "rgba(0, 0, 0, 0.7)",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.8)",
                  },
                  mt: 2,
                }}
              >
                注册
              </ActionButton>
            </form>
          </FormSide>

          {/* 登录表单 - 右侧 */}
          <FormSide>
            <IconContainer>
              <LoginIcon
                fontSize="large"
                sx={{ color: "rgba(255, 255, 255, 0.9)", position: "relative", zIndex: 1 }}
              />
            </IconContainer>
            <Title variant="h1">管理员登录</Title>
            <Subtitle variant="subtitle1">请输入您的账号和密码</Subtitle>

            {loginError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(253, 237, 237, 0.8)",
                }}
              >
                {loginError}
              </Alert>
            )}

            {loginMessage && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(229, 246, 253, 0.8)",
                }}
              >
                {loginMessage}
              </Alert>
            )}

            {loginSuccess && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(237, 247, 237, 0.8)",
                }}
              >
                {loginMessage}
              </Alert>
            )}

            <form onSubmit={handleLoginSubmit}>
              <StyledTextField
                label="用户名"
                type="text"
                fullWidth
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />

              <StyledTextField
                label="密码"
                type="password"
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <ActionButton
                type="submit"
                variant="contained"
                disabled={loginLoading}
                sx={{
                  background: "rgba(0, 0, 0, 0.7)",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.8)",
                  },
                  mt: 2,
                }}
              >
                {loginLoading ? "登录中..." : "登录"}
              </ActionButton>
            </form>

            <Typography
              variant="body2"
              sx={{
                mt: "auto",
                pt: 2,
                textAlign: "center",
                color: "rgba(255, 255, 255, 0.7)",
                fontSize: "0.85rem",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                position: "relative",
                zIndex: 1,
              }}
            >
              © 2024 教会管理系统 | 版权所有
            </Typography>
          </FormSide>
        </GlassCard>

        {/* 滑动覆盖层 - 现在是 CardWrapper 的直接子元素 */}
        <SlideOverlay
          sx={{
            transform: isRightPanelActive
              ? "translateX(calc(100% - 10px))" // 移动到右侧，考虑10px的偏移
              : "translateX(0)", // 左侧位置
            borderRadius: isRightPanelActive
              ? "24px 0 0 24px" // 右侧时左边圆角
              : "0 24px 24px 0", // 左侧时右边圆角
          }}
        >
          {isRightPanelActive ? (
            // 左侧覆盖内容 - 当覆盖层在右侧时显示
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                已有账号？
              </Typography>
              <Typography sx={{ mb: 3, maxWidth: 300 }}>
                如果您已经拥有管理员账号，请点击下方按钮登录系统
              </Typography>
              <OverlayButton
                onClick={() => setIsRightPanelActive(false)}
                startIcon={<ArrowBackIcon />}
              >
                前往登录
              </OverlayButton>
            </>
          ) : (
            // 右侧覆盖内容 - 当覆盖层在左侧时显示
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                没有账号？
              </Typography>
              <Typography sx={{ mb: 3, maxWidth: 300 }}>
                如果您需要管理员权限，请联系系统管理员获取密保码后注册账号
              </Typography>
              <OverlayButton
                onClick={() => setIsRightPanelActive(true)}
                endIcon={<ArrowForwardIcon />}
              >
                前往注册
              </OverlayButton>
            </>
          )}
        </SlideOverlay>
      </CardWrapper>
    </AuthContainer>
  );
};

export default AuthPage;
